import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

// Accept any short snake_case kind so trackConversion() calls (product_cta_click,
// read_start, share_click, etc.) are never silently rejected by the validator.
// The dashboard groups by kind, so free-form kinds are safe.
const KIND = z.string().min(1).max(48).regex(/^[a-z0-9][a-z0-9_:.-]*$/);

export const trackPageView = createServerFn({ method: "POST" })
  .inputValidator((data: { path: string; kind?: string; referrer?: string; session_id?: string; visitor_id?: string; is_internal?: boolean }) =>
    z
      .object({
        path: z.string().min(1).max(500),
        kind: KIND.optional(),
        referrer: z.string().max(500).optional(),
        session_id: z.string().max(64).optional(),
        visitor_id: z.string().max(64).optional(),
        is_internal: z.boolean().optional(),
      })
      .parse(data),
  )
  .handler(async ({ data }) => {
    const sb = createClient<Database>(
      process.env.SUPABASE_URL!,
      process.env.SUPABASE_PUBLISHABLE_KEY!,
      { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
    );
    // Best-effort; never throw to the client.
    await sb.from("page_views").insert({
      path: data.path,
      kind: data.kind ?? null,
      referrer: data.referrer ?? null,
      session_id: data.session_id ?? null,
      visitor_id: data.visitor_id ?? null,
      is_internal: data.is_internal === true,
    } as any);
    return { ok: true };
  });

export type AnalyticsSummary = {
  totals: { all: number; last7: number; last30: number };
  byKind: { kind: string; count: number }[];
  topPages: { path: string; count: number }[];
  topReferrers: { referrer: string; count: number }[];
  conversions: {
    newsletter: number;
    waitlist: number;
    shop_visits: number;
    collection_visits: number;
    outbound: number;
  };
  daily: { day: string; count: number }[];
};

export const getAnalyticsSummary = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<AnalyticsSummary> => {
    const sb = context.supabase;
    const { data: isAdmin } = await sb.rpc("has_role", {
      _user_id: context.userId,
      _role: "admin",
    });
    if (!isAdmin) throw new Error("Forbidden");

    const since30 = new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString();
    const since7 = new Date(Date.now() - 7 * 24 * 3600 * 1000).toISOString();

    const { data: rows } = await sb
      .from("page_views")
      .select("path,kind,referrer,created_at")
      .gte("created_at", since30)
      .order("created_at", { ascending: false })
      .limit(20000);

    const r = rows ?? [];
    const all = r.length;
    const last7 = r.filter((x) => x.created_at >= since7).length;

    const tally = <K extends string>(key: K) => {
      const m = new Map<string, number>();
      for (const row of r) {
        const v = (row as Record<string, string | null>)[key] ?? "";
        if (!v) continue;
        m.set(v, (m.get(v) ?? 0) + 1);
      }
      return [...m.entries()].sort((a, b) => b[1] - a[1]);
    };

    const byKindArr = tally("kind").map(([kind, count]) => ({ kind, count }));
    const topPages = tally("path")
      .slice(0, 20)
      .map(([path, count]) => ({ path, count }));
    const topReferrers = tally("referrer")
      .slice(0, 20)
      .map(([referrer, count]) => ({ referrer, count }));

    const kindCount = (k: string) => byKindArr.find((x) => x.kind === k)?.count ?? 0;

    // Daily buckets last 14 days
    const dayMap = new Map<string, number>();
    for (let i = 13; i >= 0; i--) {
      const d = new Date(Date.now() - i * 86400000).toISOString().slice(0, 10);
      dayMap.set(d, 0);
    }
    for (const row of r) {
      const d = row.created_at.slice(0, 10);
      if (dayMap.has(d)) dayMap.set(d, (dayMap.get(d) ?? 0) + 1);
    }
    const daily = [...dayMap.entries()].map(([day, count]) => ({ day, count }));

    return {
      totals: { all, last7, last30: all },
      byKind: byKindArr,
      topPages,
      topReferrers,
      conversions: {
        newsletter: kindCount("newsletter_signup"),
        waitlist: kindCount("waitlist_signup"),
        shop_visits: kindCount("shop"),
        collection_visits: kindCount("collection"),
        outbound: kindCount("outbound"),
      },
      daily,
    };
  });
